import { useSyncExternalStore, useCallback } from 'react';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = 'ledzone_promo_cart';
const LEGACY_KEY = 'promo_cart';
const TVA = 0.081;

export const fmt = (n) => Number(n || 0).toFixed(2);
export const imgUrl = (u) => (u && u.startsWith('/') ? `${BACKEND_URL}${u}` : u || '');

export const tierFor = (offer, qty) => {
  const tiers = offer.tiers || [];
  return [...tiers].reverse().find((t) => qty >= t.min) || tiers[0];
};

export const nextTier = (offer, qty) => (offer.tiers || []).find((t) => t.min > qty);

// Un seul panier promo partagé par tous les composants (en-tête, page Promotions, page Panier) et tous les onglets
const read = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const merged = {};
    (Array.isArray(raw) ? raw : []).forEach((l) => {
      const q = parseInt(l?.quantity, 10);
      if (l?.slug && q > 0) merged[l.slug] = (merged[l.slug] || 0) + q;
    });
    return Object.entries(merged).map(([slug, quantity]) => ({ slug, quantity }));
  } catch { return []; }
};

let lines = (() => {
  try { localStorage.removeItem(LEGACY_KEY); } catch { /* ignoré */ }
  return read();
})();
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const write = (next) => {
  lines = next;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignoré */ }
  emit();
};
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => { if (e.key === STORAGE_KEY) { lines = read(); emit(); } });
}
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const snapshot = () => lines;

export const promoCartStore = {
  setQty: (slug, quantity, max) => {
    const q = Math.max(0, Math.min(parseInt(quantity, 10) || 0, Number.isFinite(max) ? max : Infinity));
    const rest = lines.filter((l) => l.slug !== slug);
    const idx = lines.findIndex((l) => l.slug === slug);
    if (q <= 0) return write(rest);
    const next = [...lines];
    if (idx >= 0) next[idx] = { slug, quantity: q }; else next.push({ slug, quantity: q });
    return write(next);
  },
  clear: () => write([]),
};

export function usePromoCart() {
  const current = useSyncExternalStore(subscribe, snapshot);
  const qtyOf = useCallback((slug) => current.find((l) => l.slug === slug)?.quantity || 0, [current]);
  const add = useCallback((slug, quantity, max) => promoCartStore.setQty(slug, qtyOf(slug) + quantity, max), [qtyOf]);
  const count = current.reduce((s, l) => s + l.quantity, 0);
  return { lines: current, qtyOf, setQty: promoCartStore.setQty, add, clear: promoCartStore.clear, count };
}

const round5 = (x) => Math.round(Math.round(x * 100) / 5) * 5 / 100;

// Estimation immédiate (mêmes règles que le serveur) ; le serveur confirme et fait foi pour le paiement
export const localQuote = (lines, bySlug) => {
  const items = lines.filter((l) => bySlug[l.slug]).map((l) => {
    const o = bySlug[l.slug];
    const t = tierFor(o, l.quantity);
    return { offer_slug: l.slug, quantity: l.quantity, unit_price: t.price_ht, unit_price_ttc: t.price_ttc,
      line_total_ht: Math.round(t.price_ht * l.quantity * 100) / 100 };
  });
  const subtotal = Math.round(items.reduce((s, i) => s + i.line_total_ht, 0) * 100) / 100;
  const shippingHt = 0;
  const totalHt = Math.round((subtotal + shippingHt) * 100) / 100;
  const totalTtc = round5(totalHt * (1 + TVA));
  return { items, subtotal_ht: subtotal, shipping_ht: shippingHt, shipping_ttc: shippingHt ? 15 : 0, total_ht: totalHt,
    tva_rate: 8.1, tva_amount: Math.round((totalTtc - totalHt) * 100) / 100, total_ttc: totalTtc, estimated: true };
};
